import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@mlm/ui/components/toast';
import {
  COACH_URL,
  coaching,
  getPublicConfig,
  getReferralCode,
  type Action,
  type CoachingTier,
  type FeatureState,
} from './api';
import { inviteLink } from './invite-link';
import { DEFAULT_TIER_LABELS } from './pro';
import type { CoachingAccessKey } from '@mlm/shared';

/** Everything coaching lives under one key root, so one invalidation refreshes every screen. */
export const qk = {
  root: ['coaching'] as const,
  today: ['coaching', 'today'] as const,
  profile: ['coaching', 'profile'] as const,
  score: ['coaching', 'score'] as const,
  mood: (days: number) => ['coaching', 'mood', days] as const,
  wins: (days: number) => ['coaching', 'wins', days] as const,
  chart: ['coaching', 'chart'] as const,
  guidance: ['coaching', 'guidance'] as const,
  karma: ['coaching', 'karma'] as const,
  futureSelf: ['coaching', 'future-self'] as const,
  alignmentTodos: ['coaching', 'alignment-todos'] as const,
  goals: (status: 'active' | 'all') => ['coaching', 'goals', status] as const,
  goal: (id: string) => ['coaching', 'goal', id] as const,
  actions: (from: string, to: string) => ['coaching', 'actions', from, to] as const,
  journal: ['coaching', 'journal'] as const,
  journalPrompt: ['coaching', 'journal', 'prompt'] as const,
  affirmations: ['coaching', 'affirmations'] as const,
  entitlement: ['coaching', 'entitlement'] as const,
  creditEntries: ['coaching', 'credit-entries'] as const,
  plans: ['coaching', 'plans'] as const,
  // Shop. Namespaced away from 'coaching' because these invalidate on their own
  // cadence (a checkout, not a day rolling over).
  shop: (search: string) => ['shop', 'catalog', search] as const,
  // The Shop's own feed leaves course products out; a different key than the
  // full feed Browse reads, or one would be served from the other's cache.
  shopGoods: (search: string) => ['shop', 'catalog', search, 'goods'] as const,
  shopProduct: (id: string) => ['shop', 'product', id] as const,
  shopContext: ['shop', 'context'] as const,
  myOrders: ['shop', 'orders'] as const,
  myCourses: ['learn', 'courses'] as const,
  coursePlayer: (id: string) => ['learn', 'player', id] as const,
  courseCatalog: (search: string) => ['learn', 'catalog', search] as const,
  coursePage: (productId: string) => ['learn', 'course', productId] as const,
  mySubscriptions: ['learn', 'subscriptions'] as const,
  /** The anonymous product feed: a visitor's course tiles. */
  publicFeed: ['learn', 'public-feed'] as const,
  mySlots: ['wallet', 'slots'] as const,
  walletTotals: ['wallet', 'totals'] as const,
  walletBalance: (slotId: string | null) => ['wallet', 'balance', slotId] as const,
  walletLedger: (slotId: string | null) => ['wallet', 'ledger', slotId] as const,
  cashoutWindow: ['wallet', 'cashout', 'window'] as const,
  payoutMethods: ['wallet', 'cashout', 'methods'] as const,
  payoutQrs: ['wallet', 'cashout', 'qrs'] as const,
  payoutRequests: ['wallet', 'cashout', 'requests'] as const,
  genealogy: (slotId: string, view: string) => ['network', 'tree', slotId, view] as const,
  myCodes: ['vault', 'codes'] as const,
  activationTerms: ['vault', 'terms'] as const,
  topupPolicy: ['wallet', 'topup', 'policy'] as const,
  myTopups: ['wallet', 'topup', 'mine'] as const,
  breathwork: ['coaching', 'breathwork'] as const,
  frequencies: ['coaching', 'frequencies'] as const,
  library: (q: string, kind: string | null, category: string | null) =>
    ['coaching', 'library', q, kind, category] as const,
  libraryItem: (id: string) => ['coaching', 'library', 'item', id] as const,
  mantras: ['coaching', 'mantras'] as const,
  meditations: ['coaching', 'meditations'] as const,
  manifestation: ['coaching', 'manifestation'] as const,
  prompts: ['coaching', 'prompts'] as const,
  programs: ['coaching', 'programs'] as const,
  program: (idOrKey: string) => ['coaching', 'program', idOrKey] as const,
  programDay: (id: string, dayNo: number) => ['coaching', 'program', id, 'day', dayNo] as const,
  /** A day's audio URL sits outside the coaching root for the same reason a track's does. */
  programStream: (id: string, dayNo: number) => ['program-stream', id, dayNo] as const,
  threeSixNine: ['coaching', '369'] as const,
  visionBoard: ['coaching', 'vision-board'] as const,
  weeklyReview: (weekStart: string | null) => ['coaching', 'weekly-review', weekStart] as const,
  /**
   * A track's playback URL sits OUTSIDE the coaching root on purpose: a refresh
   * after recording the practice would otherwise swap the URL mid-listen and
   * restart the audio.
   */
  frequencyStream: (id: string) => ['frequency-stream', id] as const,
  /** The member's own referral code; account data, outside the coaching root. */
  referralCode: ['me', 'referral-code'] as const,
  /** The store, for buying a plan. Outside the coaching root: not coaching data. */
  paymentMethods: ['store', 'payment-methods'] as const,
  order: (id: string) => ['store', 'order', id] as const,
  /** Master Kala lives on the assistant module: its keys sit outside the coaching root. */
  kala: ['kala', 'persona'] as const,
  kalaConversations: ['kala', 'conversations'] as const,
  kalaThread: (id: string) => ['kala', 'thread', id] as const,
};

export const useToday = () => useQuery({ queryKey: qk.today, queryFn: coaching.today });
export const useProfile = () => useQuery({ queryKey: qk.profile, queryFn: coaching.profile });

/**
 * The tenant's public config, the same read (and cache entry) the app boots
 * from, so a screen gets the signup and Learn switches without another call.
 */
export const usePublicConfig = () =>
  useQuery({ queryKey: ['public-config'], queryFn: getPublicConfig, staleTime: 5 * 60_000 });
export const useActiveGoals = () =>
  useQuery({ queryKey: qk.goals('active'), queryFn: () => coaching.goals('active') });

/**
 * The member's plan and what it unlocks. Every gated screen reads this rather
 * than guessing from a 402, so a lock renders BEFORE a refused request rather
 * than after one. It sits under the coaching root, so buying a plan and
 * refreshing the app converge on the same invalidation.
 */
export const useEntitlement = () =>
  useQuery({ queryKey: qk.entitlement, queryFn: coaching.entitlement, staleTime: 60_000 });

export const useIsPro = () => useEntitlement().data?.tier !== 'free';

/** What the tiers are called here, with the defaults until the entitlement has loaded. */
export function useTierLabels(): Record<CoachingTier, string> {
  return useEntitlement().data?.tierLabels ?? DEFAULT_TIER_LABELS;
}

/**
 * The name of the lowest plan that opens `key`, for a lock or a "with …" row.
 * The first paid tier is assumed until the entitlement says otherwise, which
 * is what every lock said before plans could differ.
 */
export function useFeatureLabel(key: CoachingAccessKey): string {
  const e = useEntitlement().data;
  const labels = e?.tierLabels ?? DEFAULT_TIER_LABELS;
  return e?.featureTiers?.[key] === 'elite' ? labels.elite : labels.pro;
}

/**
 * No free tier: with the tenant's `paid_only` on, a member with no live plan is
 * walled behind the paywall. `null` while the entitlement is still loading, so
 * a guard can wait rather than flash the wall.
 */
export function useWalled(): boolean | null {
  const e = useEntitlement();
  if (!e.data) return null;
  return e.data.paidOnly === true && e.data.tier === 'free';
}

/**
 * Is the chart and daily guidance behind Pro for THIS member? A tenant setting
 * plus a free plan. The queries below skip the request entirely when it is, so
 * a locked screen never fires a call it knows will be refused.
 */
/**
 * What this member's plan does with one feature: `on`, `locked` (a higher plan
 * opens it) or `off` (the tenant switched it off, so there is nothing to sell
 * and the entry should not be shown at all).
 *
 * The server resolves this per feature now, so a screen no longer has to know
 * which group it used to belong to. While the entitlement is still loading the
 * answer is `on`, so a screen renders rather than flashing a lock it may not
 * need; the server refuses anyway if the optimism was wrong.
 */
export function useFeature(key: CoachingAccessKey): FeatureState {
  const e = useEntitlement().data;
  return e?.features?.[key] ?? 'on';
}

/** `true` when the member cannot use `key`, whether locked or switched off. */
export function useFeatureLocked(key: CoachingAccessKey): boolean {
  return useFeature(key) !== 'on';
}

/*
 * The tool libraries take `enabled` so Home, which shows every collection at
 * once, can skip the ones this member's plan does not open (the hubs fetch
 * regardless and render the lock).
 */
export const useBreathwork = (enabled = true) =>
  useQuery({
    queryKey: qk.breathwork,
    queryFn: coaching.breathwork,
    staleTime: 5 * 60_000,
    enabled,
  });

export const useFrequencies = (enabled = true) =>
  useQuery({
    queryKey: qk.frequencies,
    queryFn: coaching.frequencies,
    staleTime: 5 * 60_000,
    enabled,
  });

export const useFutureSelf = () =>
  useQuery({ queryKey: qk.futureSelf, queryFn: coaching.futureSelf });

export const useAlignmentTodos = () =>
  useQuery({ queryKey: qk.alignmentTodos, queryFn: coaching.alignmentTodos });

export const useKarma = () =>
  useQuery({ queryKey: qk.karma, queryFn: coaching.karma, staleTime: 10 * 60_000 });

export const useMantras = (enabled = true) =>
  useQuery({ queryKey: qk.mantras, queryFn: coaching.mantras, staleTime: 5 * 60_000, enabled });

export const useMeditations = (enabled = true) =>
  useQuery({
    queryKey: qk.meditations,
    queryFn: coaching.meditations,
    staleTime: 5 * 60_000,
    enabled,
  });

export const useManifestationPractices = (enabled = true) =>
  useQuery({
    queryKey: qk.manifestation,
    queryFn: coaching.manifestation,
    staleTime: 5 * 60_000,
    enabled,
  });

export const useCoachPrompts = () =>
  useQuery({ queryKey: qk.prompts, queryFn: coaching.prompts, staleTime: 5 * 60_000 });

export const usePrograms = (enabled = true) =>
  useQuery({ queryKey: qk.programs, queryFn: coaching.programs, staleTime: 60_000, enabled });

export const useProgram = (idOrKey: string) =>
  useQuery({ queryKey: qk.program(idOrKey), queryFn: () => coaching.program(idOrKey) });

/** One program day. `enabled` waits for /coaching/today, which creates the day's action first. */
export const useProgramDay = (id: string, dayNo: number, enabled: boolean) =>
  useQuery({
    queryKey: qk.programDay(id, dayNo),
    queryFn: () => coaching.programDay(id, dayNo),
    enabled,
    retry: false,
  });

/** The program the member is in, for the Home card; nothing is fetched while the feature is off or locked. */
export function useActiveProgram() {
  const state = useFeature('programs');
  const programs = usePrograms(state === 'on');
  return programs.data?.find((p) => p.enrollment?.status === 'active') ?? null;
}

export const useReferralCode = () =>
  useQuery({ queryKey: qk.referralCode, queryFn: getReferralCode, staleTime: 10 * 60_000 });

/** The member's invite link on the app's public url, or null until the code has loaded. */
export function useInviteLink(): string | null {
  return inviteLink(COACH_URL, useReferralCode().data?.shareRef);
}

export function useRefreshCoaching() {
  const qc = useQueryClient();
  return useCallback(() => qc.invalidateQueries({ queryKey: qk.root }), [qc]);
}

/** Done or pending for one action, with the quiet acknowledgement and a refresh of every coaching screen. */
export function useToggleAction() {
  const refresh = useRefreshCoaching();
  return useMutation({
    mutationFn: (a: Action) =>
      coaching.updateAction(a.id, { status: a.status === 'done' ? 'pending' : 'done' }),
    onSuccess: (action) => {
      if (action.status === 'done') toast.success('Done. One step closer.');
      void refresh();
    },
  });
}

/**
 * Today's Vedic guidance. Reading it is the day's alignment practice, recorded
 * once per mount of whichever screen shows it (the phone page or the desktop Align hub).
 */
export function useGuidance() {
  const locked = useFeatureLocked('guidance');
  const guidance = useQuery({
    queryKey: qk.guidance,
    queryFn: coaching.guidance,
    retry: false,
    enabled: !locked,
  });
  const refresh = useRefreshCoaching();
  const recorded = useRef(false);

  useEffect(() => {
    if (!guidance.data || recorded.current) return;
    recorded.current = true;
    coaching.event('vedic_guidance_viewed');
    void coaching
      .practice('vedic_guidance')
      .then(() => refresh())
      .catch(() => undefined);
  }, [guidance.data, refresh]);

  return guidance;
}

/** The member's natal chart; `chart_viewed` fires once per mount. */
export function useChart() {
  const locked = useFeatureLocked('chart');
  const chart = useQuery({
    queryKey: qk.chart,
    queryFn: coaching.chart,
    retry: false,
    enabled: !locked,
  });
  const viewed = useRef(false);
  useEffect(() => {
    if (!chart.data || viewed.current) return;
    viewed.current = true;
    coaching.event('chart_viewed');
  }, [chart.data]);
  return chart;
}

/**
 * Reading today's biorhythm is the day's biorhythm practice. It is recorded
 * once, and only when `ready` (a reading exists and the tool is open to this
 * member), so opening the screen twice never counts twice.
 */
export function useBiorhythmPractice(ready: boolean, alreadyDone: boolean) {
  const refresh = useRefreshCoaching();
  const recorded = useRef(false);
  useEffect(() => {
    if (!ready || recorded.current) return;
    recorded.current = true;
    coaching.event('biorhythm_viewed');
    if (alreadyDone) return;
    void coaching
      .practice('biorhythm')
      .then(() => refresh())
      .catch(() => undefined);
  }, [ready, alreadyDone, refresh]);
}
